<template>
  <ReportPanel>
    <h6 class="q-mt-md">Generate the Te Ara Tuhono report from the latest Paua data.</h6>

    <ReportPeriodSelector
      :period-type="periodType"
      :period-options="periodOptions"
      :month="month"
      :start-date="startDate"
      :end-date="endDate"
      @update:period-type="periodType = $event"
      @update:month="month = $event"
      @update:start-date="startDate = $event"
      @update:end-date="endDate = $event"
    />

    <FinancialYearQuarterSelector
      v-if="periodType === 'quarter'"
      :selected-financial-year="selectedFinancialYear"
      :selected-quarter="selectedQuarter"
      :financial-years="financialYears"
      :quarter-options="quarterOptions"
      :show-quarter="true"
      @update:selected-financial-year="selectedFinancialYear = $event"
      @update:selected-quarter="selectedQuarter = $event"
    />

    <q-btn
      :loading="loading"
      label="Generate Te Ara Report"
      color="secondary"
      no-caps
      class="q-mt-md full-width"
      @click="generateTeAra"
    />
  </ReportPanel>
</template>

<script>
import api from "@/services/api/api";
import notify from "@/services/util/notify";
import { promptDownload } from "@/services/util/file";
import FinancialYearQuarterSelector from "./FinancialYearQuarterSelector.vue";
import ReportPanel from "./ReportPanel.vue";
import ReportPeriodSelector from "./ReportPeriodSelector.vue";

export default {
  name: "TeAraReportPanel",
  components: {
    FinancialYearQuarterSelector,
    ReportPanel,
    ReportPeriodSelector
  },
  props: {
    financialYears: {
      type: Array,
      required: true
    },
    quarterOptions: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      periodType: "quarter",
      periodOptions: [
        { label: "Quarter", value: "quarter" },
        { label: "Month", value: "month" },
        { label: "Custom Dates", value: "custom" }
      ],
      selectedQuarter: null,
      selectedFinancialYear: 2024,
      month: null,
      startDate: null,
      endDate: null
    }
  },
  methods: {
    async generateTeAra() {
      this.loading = true;

      try {
        const payload = this.getPayload();
        const report = await api.reports.generateTeAra(payload);
        promptDownload(report, this.getFileName());
      } catch (error) {
        console.error(error);
        if (error.isValidationError) {
          notify.error(error.message, "top");
        } else {
          notify.error("There was an issue generating your Te Ara report.", "top");
        }
      } finally {
        this.loading = false;
      }
    },

    getPayload() {
      if (this.periodType === "quarter") {
        if (this.selectedQuarter == null) {
          const error = new Error("Please select a quarter");
          error.isValidationError = true;
          throw error;
        }

        return {
          period_type: this.periodType,
          quarter: this.selectedQuarter,
          selectedFinancialYear: this.selectedFinancialYear
        };
      }

      if (this.periodType === "month") {
        if (!this.month) {
          const error = new Error("Please select a month");
          error.isValidationError = true;
          throw error;
        }

        return {
          period_type: this.periodType,
          month: this.month
        };
      }

      if (!this.startDate || !this.endDate) {
        const error = new Error("Please select a start date and end date");
        error.isValidationError = true;
        throw error;
      }

      return {
        period_type: this.periodType,
        start_date: this.startDate,
        end_date: this.endDate
      };
    },

    getFileName() {
      if (this.periodType === "quarter") {
        return `Te Ara Tuhono Quarterly Report FY${this.selectedFinancialYear} Q${this.selectedQuarter}.xlsx`;
      }

      if (this.periodType === "month") {
        return `Te Ara Tuhono Monthly Report ${this.month}.xlsx`;
      }

      return `Te Ara Tuhono Custom Date Report ${this.startDate} to ${this.endDate}.xlsx`;
    }
  }
}
</script>
